import { useEffect, useState } from 'react';
import { HERO_MEDIA } from '../config/heroMedia';
import { isThemePreselectVisible } from '../lib/themePreselect';

/**
 * Hero background video: never on mobile / reduced-motion / theme preselect.
 * Desktop: mount only after idle so it cannot steal LCP bandwidth.
 */
export function useDeferredHeroVideo(enabled: boolean): boolean {
  const [playVideo, setPlayVideo] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setPlayVideo(false);
      return;
    }

    const canPlay = () => {
      if (typeof window === 'undefined') return false;
      if (isThemePreselectVisible()) return false;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
      if (!window.matchMedia(`(min-width: ${HERO_MEDIA.desktopMinWidthPx}px)`).matches) {
        return false;
      }
      return true;
    };

    if (!canPlay()) {
      setPlayVideo(false);
      return;
    }

    const enable = () => {
      if (canPlay()) setPlayVideo(true);
    };

    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };

    let idleId: number | undefined;
    let timeoutId: number | undefined;

    const schedule = () => {
      if (typeof w.requestIdleCallback === 'function') {
        idleId = w.requestIdleCallback(enable, {
          timeout: HERO_MEDIA.videoIdleTimeoutMs,
        });
      } else {
        timeoutId = window.setTimeout(enable, HERO_MEDIA.videoIdleTimeoutMs);
      }
    };

    if (document.readyState === 'complete') {
      schedule();
    } else {
      window.addEventListener('load', schedule, { once: true });
    }

    const onResize = () => {
      if (!canPlay()) setPlayVideo(false);
    };
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('load', schedule);
      window.removeEventListener('resize', onResize);
      if (idleId !== undefined) w.cancelIdleCallback?.(idleId);
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, [enabled]);

  return playVideo;
}
